import { Component, OnInit, TemplateRef, ViewChild, OnDestroy, DestroyRef, inject } from '@angular/core';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { Overlay } from '@angular/cdk/overlay';
import { RoutineDetonatorDeviceModalComponent } from './routine-detonator-device-modal.component';
import { RoutineSelectorModalComponent } from './routine-selector-modal.component';
import { CommonModule } from '@angular/common';
import { Routine } from 'app/modules/admin/models/routines.types';
import { User } from 'app/modules/admin/models/users.types';
import { FieldGridComponent } from '@/common/components/field-grid/field-grid.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RoutineService } from 'app/modules/admin/services/routines.service';
import { NgFor, NgIf } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { CoachDevicesService } from '@/services/coach-devices.service';
import { Device, DeviceStatus } from 'app/modules/admin/models/devices.types';
import { forkJoin } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { FormsModule } from '@angular/forms';
import { WebSocketService, RoutineDetonatorEvent } from '../../services/websocket.service';
import { firstValueFrom, take } from 'rxjs';
import Swal from 'sweetalert2';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatButton } from '@angular/material/button';
import { ZXingScannerModule } from '@zxing/ngx-scanner';
import { ChangeDetectorRef, effect } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

// Servicios y utilidades refactorizados
import { RoutineDetonatorStateService } from './services/routine-detonator-state.service';
import { RoutineExecutionService } from './services/routine-execution.service';
import { DeviceAssignmentService } from './services/device-assignment.service';
import * as Utils from './utils/routine-detonator.utils';

@Component({
    selector: 'app-routine-detonator',
    imports: [
        CommonModule,
        FieldGridComponent,
        MatProgressSpinner,
        NgFor,
        MatSelectModule,
        MatOptionModule,
        MatIconModule,
        FormsModule,
        MatExpansionModule,
        MatDialogModule,
        MatSnackBarModule,
        MatButton,
        NgIf,
        ZXingScannerModule,
        MatSlideToggleModule
    ],
    providers: [
        RoutineDetonatorStateService,
        RoutineExecutionService,
        DeviceAssignmentService
    ],
    templateUrl: './routine-detonator.component.html',
    styleUrl: './routine-detonator.component.scss'
})
export class RoutineDetonatorComponent implements OnInit, OnDestroy {
    // ViewChild references
    @ViewChild('playerModal') playerModal!: TemplateRef<any>;
    @ViewChild('deviceModal') deviceModal!: TemplateRef<any>;

    // Dialog state
    dialogRef: MatDialogRef<any> | null = null;

    // Data arrays
    routines: Routine[] = [];
    players: User[] = [];
    devices: Device[] = [];
    devicesStatus: DeviceStatus[] = [];
    availableDevices: Device[] = [];

    // UI state
    headerScrolled: boolean = false;
    isLoading: boolean = true;
    hasCamera: boolean = false;
    isScanning: boolean = false;
    isProcessing: boolean = false;
    needUseSelectPlayer: boolean = false;
    isMobile: boolean = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

    // Modal state
    currentRoutineIndex: number | null = null;
    modalRoutineIndex: number | null = null;
    modalPosition: string | null = null;
    modalCurrentDeviceId: number | null = null;

    // Control de verificación de rutinas
    private hasCheckedRoutineLogs: boolean = false;

    // Control de si el componente está activo/visible
    private isComponentActive: boolean = false;

    // Timestamp de cuando el componente se inicializa (para ignorar eventos antiguos)
    private componentInitTimestamp: number = 0;

    // DestroyRef para auto-cleanup
    private destroyRef = inject(DestroyRef);

    // Getters para acceder al estado
    get selectedRoutines(): Routine[] {
        return this.stateService.getSelectedRoutines();
    }

    get src(): string {
        return 'assets/images/cluster/Plano_Campo.webp';
    }

    get numCols(): number {
        return 7;
    }

    get alt(): string {
        return 'Plano campo fútbol';
    }

    constructor(
        private routineService: RoutineService,
        private deviceService: CoachDevicesService,
        private wsService: WebSocketService,
        private cdRef: ChangeDetectorRef,
        private snackBar: MatSnackBar,
        public dialog: MatDialog,
        private overlay: Overlay,
        private stateService: RoutineDetonatorStateService,
        private executionService: RoutineExecutionService,
        private deviceAssignmentService: DeviceAssignmentService
    ) {
        //console.log('🏗️ Constructor: RoutineDetonator creado');

        effect(() => {
            const event = this.wsService.routineDetonatorEvent();

            if (!event) {
                return; // No hay evento, salir
            }

            //console.log(`📡 Effect disparado - Evento: ${event.type}, Timestamp: ${event.timestamp}, Init: ${this.componentInitTimestamp}, Componente activo: ${this.isComponentActive}`);

            // Ignorar eventos que llegaron ANTES de que el componente se inicializara
            if (event.timestamp < this.componentInitTimestamp) {
                //console.log(`⏰ Evento ANTIGUO ignorado (timestamp ${event.timestamp} < init ${this.componentInitTimestamp})`);
                // Limpiar el evento para evitar que se procese de nuevo
                this.wsService.clearRoutineDetonatorEvent();
                return;
            }

            // Solo procesar eventos si el componente está activo
            if (this.isComponentActive) {
                //console.log('✅ Procesando evento (componente activo):', event.type);
                this.handleRoutineDetonatorEvent(event);
                // Limpiar el evento inmediatamente después de procesarlo
                this.wsService.clearRoutineDetonatorEvent();
            } else {
                //console.log('⛔ Evento IGNORADO (componente inactivo):', event.type);
                // Limpiar el evento para evitar que se procese de nuevo
                this.wsService.clearRoutineDetonatorEvent();
            }
        });
    }

    async ngOnInit(): Promise<void> {
        // Registrar el timestamp de inicialización PRIMERO
        this.componentInitTimestamp = Date.now();
        //console.log(`🚀 ngOnInit: Iniciando componente RoutineDetonator (timestamp: ${this.componentInitTimestamp})`);

        // Marcar componente como activo
        this.isComponentActive = true;

        // Limpiar cualquier SnackBar anterior que pudiera estar visible
        this.snackBar.dismiss();

        // Resetear el flag de verificación para permitir re-verificar rutinas al regresar
        this.hasCheckedRoutineLogs = false;
        //console.log('🔄 Flag hasCheckedRoutineLogs reseteado a false');

        await this.checkCameraAvailability();
        this.initializeData();
        this.subscribeToDeviceStatus();
        this.subscribeToConnectionStatus(); // Suscribirse al estado de conexión del WebSocket
        window.addEventListener('scroll', this.onWindowScroll, true);

        // Verificar inmediatamente si el WebSocket ya está conectado
        if (this.wsService.getConnectionStatus() && !this.hasCheckedRoutineLogs) {
            //console.log('🔌 WebSocket ya estaba conectado, verificando rutinas inmediatamente...');
            setTimeout(() => {
                if (this.isComponentActive) {
                    this.checkRoutineLogsInCache();
                }
            }, 500);
        }
    }

    ngOnDestroy(): void {
        //console.log('💀 ngOnDestroy: Destruyendo componente RoutineDetonator');

        // Marcar componente como inactivo PRIMERO (esto detiene el procesamiento en effect())
        this.isComponentActive = false;

        // Las suscripciones se limpian automáticamente con takeUntilDestroyed
        window.removeEventListener('scroll', this.onWindowScroll, true);

        // Limpiar todos los SnackBars al destruir el componente
        this.snackBar.dismiss();
    }

    /**
     * Verifica rutinas en caché solo cuando el WebSocket está conectado
     * y solo lo hace una vez
     */
    private checkRoutineLogsInCache(): void {
        // Prevenir múltiples ejecuciones
        if (this.hasCheckedRoutineLogs) {
            return;
        }

        const routinesWithLogs = this.selectedRoutines.filter(r => r.routine_log_id);
        if (routinesWithLogs.length === 0) return;

        // Verificar que el WebSocket esté conectado
        if (!this.wsService.getConnectionStatus()) {
            console.warn('⚠️ WebSocket no conectado, esperando conexión para verificar rutinas...');
            return;
        }

        // Marcar como ejecutado
        this.hasCheckedRoutineLogs = true;

        //console.log(`✅ Verificando ${routinesWithLogs.length} rutinas en caché...`);

        // Enviar mensajes con un pequeño delay para evitar sobrecarga
        routinesWithLogs.forEach((routine, index) => {
            setTimeout(() => {
                this.wsService.sendMessage({
                    type: 'check_routine_log_exists',
                    routine_log_id: routine.routine_log_id
                });
            }, index * 100); // 100ms de delay entre cada mensaje
        });
    }

    /**
     * Suscribe al estado de conexión del WebSocket
     * Cuando se conecta, verifica las rutinas en caché
     */
    private subscribeToConnectionStatus(): void {
        this.wsService.connectionStatus$
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (isConnected) => {
                    //console.log(`🔌 WebSocket estado: ${isConnected ? 'conectado' : 'desconectado'}, componente activo: ${this.isComponentActive}`);

                    // Solo verificar si el componente está activo, conectado, y no se ha verificado aún
                    if (isConnected && this.isComponentActive && !this.hasCheckedRoutineLogs) {
                        // Pequeño delay adicional para asegurar que la conexión está estable
                        setTimeout(() => {
                            // Verificar de nuevo que el componente sigue activo después del delay
                            if (this.isComponentActive) {
                                this.checkRoutineLogsInCache();
                            }
                        }, 500);
                    }
                },
                error: (err) => {
                    //console.error('Error en conexión WebSocket:', err);
                }
            });
    }

    // ==================== Lifecycle & Initialization ====================

    /**
     * Maneja los eventos recibidos del WebSocket
     */
    private handleRoutineDetonatorEvent(event: RoutineDetonatorEvent): void {
        switch (event.type) {
            case 'check_routine_log_exists_response':
                this.handleRoutineLogStatusConsult(event);
                break;
            case 'stop_routine_response':
                this.handleStopRoutineResponse(event);
                break;
            case 'routine_finished':
                this.handleRoutineLogFinished(event);
                break;
            // case 'routine_error':
            //     this.handleRoutineError(event);
            //     break;
            default:
                console.warn('Tipo de evento desconocido:', event.type);
        }
    }

    /**
     * Maneja actualizaciones de estado de rutinas
     */
    private handleRoutineLogStatusConsult(event: RoutineDetonatorEvent): void {
        // Buscar la rutina por execution_id o routine_log_id
        const routineLogId: number = event.routine_log_id;
        const routine = this.selectedRoutines.find(
            r => r.routine_log_id === routineLogId
        );

        if (event.status === false) {
            // Cambiamos ese routine_log_id y execution_id a null para que el usuario pueda reenviarla
            if (routine) {
                routine.execution_id = null;
                routine.routine_log_id = null;
            }
        }

        this.cdRef.detectChanges();
    }

    private subscribeToDeviceStatus(): void {
        this.wsService.getDevicesStatus()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (devices) => {
                    this.devicesStatus = devices;
                },
                error: (err) => {
                    //console.error('Error loading devices status:', err);
                }
            });
    }

    onWindowScroll = (): void => {
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        this.headerScrolled = scrollY > 10;
    }

    initializeData(): void {
        forkJoin({
            routinesResponse: this.routineService.getRoutinesToDetonate(),
            devices: this.deviceService.getDevices(),
            players: this.routineService.getPlayers()
        }).subscribe({
            next: ({ routinesResponse, devices, players }) => {
                this.routines = routinesResponse.routines;
                this.devices = devices;
                this.players = players;
                this.isLoading = false;

                // Validate loaded routines against loaded devices
                this.selectedRoutines.forEach(routine => {
                    if (routine.id) { // Skip empty routine
                        this.validateRoutineDevices(routine);
                    }
                });
            },
            error: (err) => {
                //console.error('Error loading data:', err);
                this.isLoading = false;
            }
        });
    }

    async checkCameraAvailability(): Promise<void> {
        try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            this.hasCamera = devices.some(
                device =>
                    device.kind === 'videoinput' &&
                    device.label.trim() !== '' &&
                    !/virtual|obs/i.test(device.label)
            );
        } catch (error) {
            console.warn('No se pudo detectar cámara:', error);
            this.hasCamera = false;
        }
    }

    // ==================== Routine Management ====================

    addRoutine(): void {
        this.openRoutineSelectionModal();
    }

    selectRoutine(routine: Routine): void {
        this.validateRoutineDevices(routine);
        this.stateService.addRoutine(routine);
    }

    onSelectChange(index: number, routineId: number): void {
        const selected = this.routines.find(r => r.id === routineId);
        if (selected) {
            this.validateRoutineDevices(selected);
            this.stateService.replaceRoutine(index, selected);
        }
    }

    validateRoutineDevices(routine: Routine): void {
        if (!routine.cluster || !routine.cluster.type_devices) return;

        let changed = false;
        const updatedTypeDevices = routine.cluster.type_devices.map(td => {
            if (td.selected_device_id) {
                const device = this.devices.find(d => d.id === td.selected_device_id);
                if (device) {
                    if (!this.deviceAssignmentService.isDeviceValid(device)) {
                        changed = true;
                        return { ...td, selected_device_id: null };
                    }
                }
            }
            return td;
        });

        if (changed) {
            // Find the index of the routine in the selected routines list
            const index = this.selectedRoutines.findIndex(r => r.id === routine.id && r.selected_user_id === routine.selected_user_id);

            if (index !== -1) {
                this.stateService.updateRoutine(index, (r) => ({
                    ...r,
                    cluster: {
                        ...r.cluster,
                        type_devices: updatedTypeDevices
                    }
                }));
            }

            this.snackBar.open('Se han desasignado dispositivos con licencia vencida o inválida.', 'Cerrar', {
                duration: 4000,
                panelClass: ['warning-snackbar']
            });
        }
    }

    removeRoutine(index: number): void {
        this.stateService.removeRoutine(index);
    }

    // ==================== Routine Execution ====================

    public someRoutineIsReady(): boolean {
        return this.stateService.hasReadyRoutines();
    }

    public async sendRoutineToWS(sendAll: boolean = false, routineIndex?: number, $event?: MouseEvent): Promise<void> {
        $event?.stopPropagation();

        const routinesToSend = sendAll
            ? Utils.getReadyRoutines(this.selectedRoutines)
            : Utils.getReadyRoutines([this.selectedRoutines[routineIndex]]);

        if (routinesToSend.length === 0) {
            this.snackBar.open('No hay rutinas listas para enviar.', 'Cerrar', { duration: 3000 });
            return;
        }

        // Generar y asignar execution_id a cada rutina antes de enviar
        routinesToSend.forEach(routine => {
            const execution_id = Utils.generateExecutionId(routine.id, routine.selected_user_id);
            routine.execution_id = execution_id;

            // También actualizar en selectedRoutines para mantener sincronización
            const selectedIndex = this.selectedRoutines.findIndex(
                r => r.id === routine.id && r.selected_user_id === routine.selected_user_id
            );
            if (selectedIndex !== -1) {
                this.stateService.updateRoutine(selectedIndex, (r) => ({
                    ...r,
                    execution_id: execution_id
                }));
            }
        });

        await this.executionService.executeRoutines(
            routinesToSend,
            this.routines,
            this.players
        );
    }

    // ==================== Device Management ====================

    getAvailableDevices(typeDeviceId: number, currentDeviceId?: number, routineSel?: Routine): Device[] {
        const divisionIds = routineSel?.divisions?.map(d => d.id);
        return this.deviceAssignmentService.getAvailableDevices(
            typeDeviceId,
            this.devices,
            this.selectedRoutines,
            currentDeviceId,
            divisionIds
        );
    }

    public getDeviceAliasMap(routineSel: any): { [key: string]: string } {
        return this.deviceAssignmentService.createDeviceAliasMap(routineSel, this.devices);
    }

    public getDeviceAlias(id: number): string {
        const device = this.devices.find(d => d.id === id);
        return Utils.getDeviceAlias(device);
    }

    // ==================== Cell Events ====================

    public onCellClick(routineIndex: number, event: { row: number, col: number }): void {
        const routine = this.selectedRoutines[routineIndex];
        const position = `${event.row}-${event.col}`;
        const typeDevice = this.deviceAssignmentService.findTypeDeviceByPosition(routine, position);

        if (!typeDevice) return;

        if (typeDevice.selected_device_id) {
            this.sendFinderByDeviceId(typeDevice.selected_device_id);
        } else {
            this.openDeviceModalByPosition(routineIndex, position);
        }
    }

    public onCellLongPress(routineIndex: number, event: { row: number, col: number }): void {
        if (!this.isMobile) return;

        const routine = this.selectedRoutines[routineIndex];
        const position = `${event.row}-${event.col}`;
        const typeDevice = this.deviceAssignmentService.findTypeDeviceByPosition(routine, position);

        if (typeDevice) {
            this.openDeviceModalByPosition(routineIndex, position);
        }
    }

    public onCellRightClick(routineIndex: number, event: { row: number, col: number, event: MouseEvent }): void {
        if (this.isMobile) return;

        event.event?.preventDefault();

        const routine = this.selectedRoutines[routineIndex];
        const position = `${event.row}-${event.col}`;
        const typeDevice = this.deviceAssignmentService.findTypeDeviceByPosition(routine, position);

        if (typeDevice) {
            this.openDeviceModalByPosition(routineIndex, position);
        }
    }

    public onDeviceCellMouseLeave(): void {
        // Placeholder for future longpress implementation
    }

    // ==================== Device Modal ====================

    public openDeviceModalByPosition(routineIndex: number, position: string): void {
        const routine = this.selectedRoutines[routineIndex];
        const typeDevice = this.deviceAssignmentService.findTypeDeviceByPosition(routine, position);

        if (!typeDevice) return;

        this.modalRoutineIndex = routineIndex;
        this.modalPosition = position;
        this.modalCurrentDeviceId = typeDevice.selected_device_id ?? null;
        this.availableDevices = this.getAvailableDevices(typeDevice.id, typeDevice.selected_device_id, routine);

        this.availableDevices = this.deviceAssignmentService.sortDevicesWithSelectedFirst(
            this.availableDevices,
            this.modalCurrentDeviceId
        );

        this.openDeviceModal();
    }

    public openDeviceModalByTypeId(routineIndex: number, typeDeviceId: number): void {
        const routine = this.selectedRoutines[routineIndex];
        const typeDevice = routine.cluster?.type_devices?.find(td => td.id === typeDeviceId);

        if (!typeDevice) return;

        this.modalRoutineIndex = routineIndex;
        this.modalCurrentDeviceId = typeDevice.selected_device_id ?? null;
        this.availableDevices = this.getAvailableDevices(typeDeviceId, typeDevice.selected_device_id, routine);

        this.openDeviceModal();
    }

    private openDeviceModal(): void {
        const dialogRef = this.dialog.open(RoutineDetonatorDeviceModalComponent, {
            width: '100%',
            maxWidth: '450px',
            height: '600px',
            panelClass: 'device-modal-panel', // Clase personalizada para quitar padding si es necesario
            data: {
                availableDevices: this.availableDevices,
                currentDeviceId: this.modalCurrentDeviceId
            },
            disableClose: true
        });

        dialogRef.componentInstance.finderRequest.subscribe((deviceId: number) => {
            this.sendFinderByDeviceId(deviceId);
        });

        dialogRef.afterClosed().subscribe(result => {
            if (!result) return;

            if (result.action === 'assign') {
                this.assignDeviceToPosition(result.deviceId);
            } else if (result.action === 'unassign') {
                this.unassignDeviceFromPosition();
            }
        });
    }

    public assignDeviceToPosition(deviceId: number): void {
        if (this.modalRoutineIndex === null || !this.modalPosition) return;

        this.stateService.assignDeviceToPosition(
            this.modalRoutineIndex,
            this.modalPosition,
            deviceId
        );
        this.cdRef.detectChanges();
    }

    public unassignDeviceFromPosition(): void {
        if (this.modalRoutineIndex === null || !this.modalPosition) return;

        this.stateService.unassignDeviceFromPosition(
            this.modalRoutineIndex,
            this.modalPosition
        );
        this.cdRef.detectChanges();
    }

    // ==================== Finder ====================

    async sendFinder(row: number, col: number, routineIndex: number): Promise<void> {
        const routine = this.selectedRoutines[routineIndex];
        const position = `${row}-${col}`;
        const typeDevice = this.deviceAssignmentService.findTypeDeviceByPosition(routine, position);

        if (typeDevice?.selected_device_id) {
            await this.sendFinderByDeviceId(typeDevice.selected_device_id);
        }
    }

    async sendFinderByDeviceId(deviceId: number): Promise<void> {
        const device = this.devices.find(d => d.id === deviceId);

        if (!device?.serial) {
            this.snackBar.open('No se encontró el dispositivo o no tiene serial', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar']
            });
            return;
        }

        const instruction = { type: 'finder', uid: device.serial };
        this.wsService.sendMessage(instruction);

        try {
            const response = await firstValueFrom(this.wsService.messages$.pipe(take(1)));
            if (response.meta.type === 'finder_response' && response.error) {
                this.snackBar.open(response.error.message || 'No se pudo encontrar el dispositivo', 'Cerrar', {
                    duration: 4000,
                    panelClass: ['error-snackbar']
                });
            }
        } catch (error) {
            //console.error(error);
            this.snackBar.open('Error de comunicación con el servidor', 'Cerrar', {
                duration: 4000,
                panelClass: ['error-snackbar']
            });
        }
    }

    // ==================== Player Management ====================

    openPlayerSelectionModal($event: MouseEvent, routineIndex: number): void {
        $event.stopPropagation();
        this.currentRoutineIndex = routineIndex;
        this.isScanning = true;
        this.isProcessing = false;
        this.dialogRef = this.dialog.open(this.playerModal, {
            width: '100%',
            maxWidth: '600px',
            disableClose: true
        });
    }

    getAvailablePlayers(currentUserId?: number): User[] {
        const routine = this.selectedRoutines[this.currentRoutineIndex];
        return Utils.getAvailablePlayers(routine, this.players, this.selectedRoutines, currentUserId);
    }

    onCodeResult(result: string): void {
        if (!this.isScanning || this.isProcessing) return;

        this.isScanning = false;
        this.isProcessing = true;

        const cleanUuid = result.replace(/'/g, '-');
        const player = this.players.find(p =>
            p.uuid === cleanUuid &&
            !Utils.isPlayerAlreadySelected(p.uuid, this.selectedRoutines, this.players)
        );

        if (player && this.currentRoutineIndex !== null) {
            this.stateService.assignUserToRoutine(this.currentRoutineIndex, player.id);
            this.cdRef.detectChanges();
            this.closeModal();
        } else {
            Swal.fire({
                title: 'Error',
                text: 'Jugador no encontrado o ya fue asignado a otra rutina.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
                confirmButtonColor: '#3b82f6'
            });
            this.isProcessing = false;
        }
    }

    onPlayerSelect(uuid: string): void {
        if (this.currentRoutineIndex === null) return;

        const player = this.players.find(p =>
            p.uuid === uuid &&
            !Utils.isPlayerAlreadySelected(uuid, this.selectedRoutines, this.players)
        );

        if (player) {
            this.stateService.assignUserToRoutine(this.currentRoutineIndex, player.id);
            this.cdRef.detectChanges();
            this.closeModal();
        } else {
            Swal.fire({
                title: 'Error',
                text: 'Jugador no encontrado o ya fue asignado a otra rutina.',
                icon: 'error',
                confirmButtonText: 'Aceptar',
                confirmButtonColor: '#3b82f6'
            });
        }
    }

    closeModal(): void {
        this.isScanning = false;
        this.isProcessing = false;
        this.dialogRef?.close();
        this.dialogRef = null;
    }

    getPlayerModalTitle(): string {
        if (!this.hasCamera || this.needUseSelectPlayer) {
            return 'Seleccionar jugador';
        }
        return 'Escanear QR';
    }

    isPlayerAlreadySelected(uuid: string): boolean {
        return Utils.isPlayerAlreadySelected(uuid, this.selectedRoutines, this.players);
    }

    // ==================== Routine Modal ====================

    openRoutineSelectionModal(): void {
        const isMobile = window.innerWidth <= 768;
        const isTablet = window.innerWidth > 768 && window.innerWidth <= 1024;

        let modalConfig: any = {
            data: { routines: this.routines },
            disableClose: false,
            hasBackdrop: true,
            backdropClass: 'routine-selector-backdrop',
            autoFocus: false,
            restoreFocus: false,
            panelClass: 'routine-modal-container',
            scrollStrategy: this.overlay.scrollStrategies.block()
        };

        if (isMobile) {
            modalConfig = {
                ...modalConfig,
                width: '100vw',
                height: '100vh',
                maxWidth: '100vw',
                maxHeight: '100vh',
                position: { top: '0', left: '0' }
            };
        } else if (isTablet) {
            modalConfig = {
                ...modalConfig,
                width: '80vw',
                maxWidth: '600px',
                height: '75vh',
                minHeight: '450px',
                maxHeight: '85vh'
            };
        } else {
            modalConfig = {
                ...modalConfig,
                width: '700px',
                maxWidth: '90vw',
                height: '70vh',
                minHeight: '500px',
                maxHeight: '80vh'
            };
        }

        const dialogRef = this.dialog.open(RoutineSelectorModalComponent, modalConfig);

        dialogRef.afterClosed().subscribe(selectedRoutine => {
            if (selectedRoutine) {
                this.selectRoutine(selectedRoutine);
            }
        });
    }

    // ==================== Utility Methods (exposed to template) ====================

    getPlayerNameById(id: number | null): string {
        return Utils.getPlayerNameById(this.players, id);
    }

    getPlayerInitials(id: number | null): string {
        const player = this.players.find(p => p.id === id);
        return Utils.getPlayerInitials(player);
    }

    getRoutineStatusColor(routine: any): string {
        return Utils.getRoutineStatusColor(routine);
    }

    getRoutineBorderClass(routine: Routine): string {
        return Utils.getRoutineBorderClass(routine);
    }

    getRoutineType(routine_type_id: number): string {
        return Utils.getRoutineType(routine_type_id);
    }

    getDeviceCount(tableData: any): number {
        return Utils.getDeviceCount(tableData);
    }

    reversePosition(position: string): string {
        return Utils.reversePosition(position);
    }

    async stopRoutine($event: MouseEvent, routineLogId: number): Promise<void> {
        $event?.stopPropagation();
        if (!routineLogId) return;
        const data = { type: 'stop_routine', routine_log_id: routineLogId };
        this.wsService.sendMessage(data);
    }

    disableTrashButton(routine: Routine): boolean {
        return routine.routine_log_id ? true : false;
    }

    private handleRoutineLogFinished(event: RoutineDetonatorEvent): void {
        // Obtener la rutina ANTES de resetearla para conservar el selected_user_id
        const routine = this.getRoutineByLogId(event.routine_log_id);
        const playerName = this.getPlayerNameById(routine?.selected_user_id || null);

        // Ahora sí resetear la rutina
        this.resetRoutineLog(event);

        // Solo mostrar SnackBar si el componente está activo
        if (!this.isComponentActive) {
            //console.log('⚠️ Componente inactivo, SnackBar no mostrado:', playerName);
            return;
        }

        // Cerrar cualquier SnackBar anterior antes de mostrar uno nuevo
        this.snackBar.dismiss();

        // Usar SnackBar en lugar de Swal para no bloquear la UI
        this.snackBar.open(
            `✅ ${playerName} ha finalizado la rutina`,
            'Cerrar',
            {
                duration: 5000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['success-snackbar']
            }
        );
    }

    /**
     * Maneja cuando una rutina se detiene
     */
    private handleStopRoutineResponse(event: RoutineDetonatorEvent): void {
        // Obtener la rutina ANTES de resetearla para conservar el selected_user_id
        const routine = this.getRoutineByLogId(event.routine_log_id);
        const playerName = this.getPlayerNameById(routine?.selected_user_id || null);

        if (!this.isComponentActive) {
            //console.log('⚠️ Componente inactivo, error no mostrado:', playerName);
            return;
        }

        if (event.success === false) {
            // Solo mostrar error si el componente está activo
            // Cerrar cualquier Swal abierto antes de mostrar uno nuevo
            Swal.close();
            Swal.fire({
                title: 'Error',
                text: `La rutina de ${playerName} no se pudo detener.`,
                icon: 'error',
                confirmButtonText: 'Aceptar',
                confirmButtonColor: '#3b82f6'
            });
            return;
        }

        // Resetear la rutina después de obtener el nombre del jugador
        this.resetRoutineLog(event);

        // Cerrar cualquier SnackBar anterior antes de mostrar uno nuevo
        this.snackBar.dismiss();

        // Usar SnackBar para éxito (no bloquea la UI)
        this.snackBar.open(
            `⏹️ Rutina de ${playerName} detenida correctamente`,
            'Cerrar',
            {
                duration: 4000,
                horizontalPosition: 'center',
                verticalPosition: 'top',
                panelClass: ['info-snackbar']
            }
        );
    }

    resetRoutineLog(event: RoutineDetonatorEvent): Routine | undefined {
        const routineLogId: number = event.routine_log_id;
        const routine = this.getRoutineByLogId(routineLogId);
        if (routine) {
            routine.execution_id = null;
            routine.routine_log_id = null;
        }
        return routine;
    }

    getRoutineByLogId(routine_log_id: number): Routine | undefined {
        return this.selectedRoutines.find(r => r.routine_log_id === routine_log_id);
    }
}
